/**
 * Where the microservice gateway is.
 *
 * NOT a constant. The gateway is called by the BROWSER, so a hardcoded localhost:5500 only
 * works for whoever is running it — a shared link would send every tester's browser looking
 * for a gateway on their own machine.
 *
 * Resolution order:
 *   1. ?gateway=<origin> on the URL, remembered afterwards. This is what lets one shared
 *      link carry the gateway with it when the tunnel hostnames are unrelated.
 *   2. localStorage (from step 1, or set by hand).
 *   3. A VS Code dev tunnel — <label>-<port>.<region>.devtunnels.ms — where the gateway is
 *      the same tunnel on port 5500, so nothing needs configuring.
 *   4. localhost:5500, so local development is exactly as it was.
 */
const GATEWAY_STORAGE_KEY = 'luqta_gateway_origin';
function resolveGatewayOrigin(): string {
  const fallback = 'https://production-luqta.xtroedge.com/';
  if (typeof window === 'undefined' || !window.location) return fallback;
  const trim = (u: string) => u.trim().replace(/\/+$/, '');
  const isLocal = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('gateway');
    if (fromUrl) {
      const v = trim(fromUrl);
      // Remembered so the parameter only has to be in the link the first time.
      try { window.localStorage.setItem(GATEWAY_STORAGE_KEY, v); } catch { /* private mode */ }
      return v;
    }
    // On localhost a remembered gateway is ignored — a stale one would silently send local
    // testing to a deployed server. Only an explicit ?gateway= overrides localhost:5500.
    if (isLocal) return 'https://production-luqta.xtroedge.com/';
    const saved = window.localStorage.getItem(GATEWAY_STORAGE_KEY);
    if (saved) return trim(saved);
  } catch {
    // A browser with site data blocked still has to reach a gateway — fall through.
  }
  // <label>-<port>.<region>.devtunnels.ms → the same tunnel, port 5500.
  const m = window.location.hostname.match(/^(.*)-(\d+)\.(.+\.devtunnels\.ms)$/i);
  if (m) return `${window.location.protocol}//${m[1]}-5500.${m[3]}`;
  return fallback;
}
const GATEWAY_ORIGIN = resolveGatewayOrigin();

export const environment = {
  production: false,
  baseUrl: 'https://dev.luqta.io',
  paymentRedirectApi: '',

  // ── the microservice gateway ────────────────────────────────────────────────
  // Resolved above, not hardcoded — see resolveGatewayOrigin().
  microUrl: GATEWAY_ORIGIN,
  // No service key lives here: this file ships in the browser bundle. Sign-in needs no key
  // and every other call is authorised by the session token.
  // The one Xano endpoint still read directly: the gateway has no action for the WhatsApp
  // Campaigns list yet, so the dashboard calls it the way the main branch does.
  whatsappCampaignsUrl: 'https://xi5f-5y62-kbyr.n7e.xano.io/api:jKhRtMpW:dev/whatsapp_pending_contests',
  Ai_insight_apiUrl: 'https://ai-dev-luqta.xtroedge.com',
  // chatboltApiUrl: 'https://backend-api-chatbolt-backend.hf.space',
  chatboltApiUrl: 'https://ai-dev-luqta.xtroedge.com',
  // ChatBolt agent API keys. Browser-visible by design (they only identify an agent; the agent's
  // allowed domains, rate limits and signed context are what protect it) — kept here so a key
  // is rotated in one place instead of hunted through components.
  chatboltKeys: {
    assistant: 'cb_2792471aa4694699a69ae6f6b8337138', // the dashboard chat widget
    endpoints: 'cb_2792471aa4694699a69ae6f6b8337138', // AI endpoints: contest suggestions, image regenerate, AI demo
    insights: 'cb_2792471aa4694699a69ae6f6b8337138', // dashboard predictions / insights
  },
  googleMapsApiKey: '',
  appVersion: '11-5-2026-v1',
  firebaseConfig: {
    apiKey: "AIzaSyC2CTC9Yut75v2lptj8YGXQkWmKxf4d6to",
    authDomain: "luqta-50841.firebaseapp.com",
    projectId: "luqta-50841",
    storageBucket: "luqta-50841.firebasestorage.app",
    messagingSenderId: "104176712263",
    appId: "1:104176712263:web:1bb54a2e6b57608f268f62",
    measurementId: "G-J6M382CML7"
  }
};




// FOR LIVE
// ng build --configuration=production    live
//    dev
// FOR DEV
// ng build
// ng serve
// For version update node scripts/version.js; ng build --configuration=development
// Production chat bolt https://xtro-edge-chatbolt-backend.hf.space
// Dev chat bolt https://backend-api-chatbolt-backend.hf.space
// Procuction chat bolt admin https://admin-ai-agent-pro.vercel.app/app/dashboard/activity
// Dev chat bolt admin https://data-chat-agent-ai-pro.vercel.app/app/agents
// For npm npm install --legacy-peer-deps

